import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Check, X, Zap, Crown, Sparkles, Building2, Loader2, 
  Star, Shield, BookOpen, Brain, Users, Headphones,
  BarChart3, Calendar, Library, Mic, ChevronDown, ChevronUp,
  Globe, MapPin
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature definitions with icons
const FEATURE_DEFINITIONS = {
  has_quick_test: { label: 'Tests Rápidos', icon: Zap, description: 'Practica con tests cortos de 10-25 preguntas' },
  has_simulacro: { label: 'Simulacros Oficiales', icon: BookOpen, description: 'Exámenes completos con formato oficial' },
  has_tutor_ia: { label: 'Tutor IA 24/7', icon: Brain, description: 'Asistente de IA para resolver dudas' },
  has_flashcards: { label: 'Flashcards FSRS', icon: Star, description: 'Sistema de repetición espaciada científico' },
  has_study_plan: { label: 'Plan de Estudio IA', icon: Calendar, description: 'Plan personalizado generado por IA' },
  has_analytics: { label: 'Estadísticas', icon: BarChart3, description: 'Análisis detallado de tu progreso' },
  has_community: { label: 'Comunidad', icon: Users, description: 'Conecta con otros opositores' },
  has_library: { label: 'Biblioteca', icon: Library, description: 'Acceso a materiales de estudio' },
  has_audios: { label: 'Audios', icon: Headphones, description: 'Resúmenes en audio para estudiar' },
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    num_employees: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API}/plans/all`);
      setPlans(res.data.plans || []);
    } catch (error) {
      console.error('Error loading plans:', error);
      // Use default plans if API fails
      setPlans([
        {
          plan_id: 'basic',
          name: 'Plan Básico',
          description: 'Para empezar a preparar tu oposición',
          price_monthly: 14.99,
          price_yearly: 149.99,
          credits_monthly: 500,
          has_tutor_ia: false,
          has_simulacro: true,
          has_quick_test: true,
          has_flashcards: false,
          has_study_plan: false,
          has_analytics: true,
          has_community: false,
          has_library: false,
          has_audios: false,
          max_exams_daily: 10,
          features: ['500 créditos/mes', 'Tests rápidos ilimitados', 'Simulacros oficiales', 'Estadísticas básicas'],
          badge_color: 'blue',
          order: 1
        },
        {
          plan_id: 'top',
          name: 'Plan Top',
          description: 'Todo lo que necesitas para aprobar',
          price_monthly: 29.99,
          price_yearly: 299.99,
          credits_monthly: 2000,
          has_tutor_ia: true,
          has_simulacro: true,
          has_quick_test: true,
          has_flashcards: true,
          has_study_plan: true,
          has_analytics: true,
          has_community: true,
          has_library: false,
          has_audios: false,
          max_exams_daily: 999,
          features: ['2.000 créditos/mes', 'Tests ilimitados', 'Tutor IA 24/7', 'Flashcards FSRS', 'Estadísticas avanzadas'],
          badge_color: 'purple',
          order: 2
        },
        {
          plan_id: 'premium',
          name: 'Plan Premium',
          description: 'Máximas posibilidades de aprobar',
          price_monthly: 49.99,
          price_yearly: 499.99,
          credits_monthly: 10000,
          has_tutor_ia: true,
          has_simulacro: true,
          has_quick_test: true,
          has_flashcards: true,
          has_study_plan: true,
          has_analytics: true,
          has_community: true,
          has_library: true,
          has_audios: true,
          max_exams_daily: 999,
          is_multi_access: false,
          features: ['10.000 créditos/mes', 'Todo lo del Plan Top', 'Biblioteca de audios y libros', 'Soporte prioritario'],
          badge_color: 'amber',
          order: 3
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId, price) => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      toast.error('Por favor inicia sesión primero');
      navigate('/auth');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/plans/subscribe`,
        { plan_id: planId, billing_period: billingPeriod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.company_name || !contactForm.contact_name || !contactForm.email) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/institutional`, contactForm);
      toast.success('¡Solicitud enviada! Nos pondremos en contacto contigo pronto.');
      setShowContactModal(false);
      setContactForm({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        num_employees: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const getPrice = (plan) => {
    if (billingPeriod === 'yearly') {
      return plan.price_yearly || plan.price_monthly * 10;
    }
    return plan.price_monthly;
  };

  const getMonthlyEquivalent = (plan) => {
    if (billingPeriod === 'yearly') {
      const yearly = plan.price_yearly || plan.price_monthly * 10;
      return (yearly / 12).toFixed(2);
    }
    return plan.price_monthly;
  };

  const getPlanIcon = (planId) => {
    switch(planId) {
      case 'basic': return Zap;
      case 'top': return Star;
      case 'premium': return Crown;
      case 'institutional': return Building2;
      default: return Sparkles;
    }
  };

  const getPlanGradient = (planId) => {
    switch(planId) {
      case 'basic': return 'from-blue-500 to-cyan-500';
      case 'top': return 'from-purple-500 to-pink-500';
      case 'premium': return 'from-amber-500 to-orange-500';
      case 'institutional': return 'from-emerald-500 to-teal-500';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const isFeatureIncluded = (plan, featureKey) => {
    return plan[featureKey] === true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const individualPlans = plans.filter(p => p.plan_id !== 'institutional' && p.is_public !== false);
  const institutionalPlan = plans.find(p => p.plan_id === 'institutional');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
      {/* Header */}
      <nav className="border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="text-white hover:bg-white/10"
              data-testid="back-home-btn"
            >
              ← Volver
            </Button>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Shield className="w-4 h-4" />
              Pago seguro con Stripe
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6"
          >
            Planes que se adaptan{' '}
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              a tus metas
            </span>
          </motion.h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Elige el plan perfecto para preparar tu oposición. Todos incluyen acceso completo al banco de preguntas.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur rounded-full p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === 'monthly' 
                  ? 'bg-white text-slate-900' 
                  : 'text-white hover:text-white/80'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === 'yearly' 
                  ? 'bg-white text-slate-900' 
                  : 'text-white hover:text-white/80'
              }`}
            >
              Anual
              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                -17%
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {individualPlans.map((plan, index) => {
            const Icon = getPlanIcon(plan.plan_id);
            const isPopular = plan.plan_id === 'top';
            const isPremium = plan.plan_id === 'premium';
            
            return (
              <motion.div
                key={plan.plan_id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-3xl overflow-hidden ${
                  isPopular 
                    ? 'ring-2 ring-purple-500 shadow-lg shadow-purple-500/20' 
                    : isPremium
                    ? 'ring-2 ring-amber-500 shadow-lg shadow-amber-500/20'
                    : ''
                }`}
                data-testid={`pricing-card-${plan.plan_id}`}
              >
                {/* Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl" />
                
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center py-2 text-sm font-bold">
                    <Sparkles className="w-4 h-4 inline mr-1" />
                    Más Popular
                  </div>
                )}
                
                {isPremium && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-2 text-sm font-bold">
                    <Crown className="w-4 h-4 inline mr-1" />
                    Mejor Valor
                  </div>
                )}

                <div className={`relative p-6 ${(isPopular || isPremium) ? 'pt-14' : ''}`}>
                  {/* Plan Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanGradient(plan.plan_id)} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <p className="text-sm text-slate-400">{plan.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-white">
                        €{getMonthlyEquivalent(plan)}
                      </span>
                      <span className="text-slate-400">/mes</span>
                    </div>
                    {billingPeriod === 'yearly' && (
                      <p className="text-sm text-green-400 mt-1">
                        Facturado €{getPrice(plan)} anualmente
                      </p>
                    )}
                    <p className="text-sm text-slate-500 mt-1">
                      {plan.credits_monthly?.toLocaleString()} créditos/mes
                    </p>
                  </div>

                  {/* Features List - What's Included */}
                  <div className="space-y-3 mb-6">
                    {Object.entries(FEATURE_DEFINITIONS).map(([key, feature]) => {
                      const included = isFeatureIncluded(plan, key);
                      const FeatureIcon = feature.icon;
                      return (
                        <div 
                          key={key}
                          className={`flex items-center gap-3 ${included ? 'text-white' : 'text-slate-500'}`}
                        >
                          {included ? (
                            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                              <Check className="w-3 h-3 text-green-400" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-slate-700/50 flex items-center justify-center">
                              <X className="w-3 h-3 text-slate-600" />
                            </div>
                          )}
                          <FeatureIcon className={`w-4 h-4 ${included ? 'text-slate-300' : 'text-slate-600'}`} />
                          <span className={`text-sm ${included ? '' : 'line-through'}`}>
                            {feature.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Multi-Access Badge for Premium */}
                  {plan.is_multi_access && (
                    <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
                      <div className="flex items-center gap-2 text-amber-300 text-sm font-medium mb-1">
                        <Globe className="w-4 h-4" />
                        Multi-Acceso Premium
                      </div>
                      <p className="text-xs text-amber-200/70">
                        Accede a múltiples oposiciones de la misma categoría en todas las comunidades autónomas
                      </p>
                    </div>
                  )}

                  {/* Expand Details */}
                  <button
                    onClick={() => setExpandedPlan(expandedPlan === plan.plan_id ? null : plan.plan_id)}
                    className="w-full flex items-center justify-center gap-2 text-slate-400 text-sm hover:text-white transition-colors mb-4"
                  >
                    {expandedPlan === plan.plan_id ? 'Ocultar detalles' : 'Ver todos los detalles'}
                    {expandedPlan === plan.plan_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  <AnimatePresence>
                    {expandedPlan === plan.plan_id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden mb-4"
                      >
                        <div className="space-y-2 p-4 rounded-xl bg-white/5 border border-white/10">
                          <h4 className="text-sm font-medium text-white mb-3">Incluye:</h4>
                          {plan.features?.map((feature, i) => (
                            <div key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                          <div className="pt-3 border-t border-white/10 mt-3">
                            <p className="text-xs text-slate-500">
                              Máximo {plan.max_exams_daily === 999 ? 'ilimitados' : plan.max_exams_daily} exámenes/día
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSelectPlan(plan.plan_id, getPrice(plan))}
                    className={`w-full py-6 rounded-xl font-bold text-lg bg-gradient-to-r ${getPlanGradient(plan.plan_id)} hover:opacity-90 transition-opacity text-white`}
                    data-testid={`select-plan-${plan.plan_id}-btn`}
                  >
                    {plan.plan_id === 'top' ? 'Comenzar Prueba Gratuita' : 'Suscribirse'}
                  </Button>
                  
                  {plan.plan_id === 'top' && (
                    <p className="text-center text-xs text-slate-500 mt-2">
                      7 días de prueba gratuita. Cancela cuando quieras.
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Comparativa de Funcionalidades
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-slate-400 font-medium">Funcionalidad</th>
                  {individualPlans.map(plan => (
                    <th key={plan.plan_id} className="text-center p-4 text-white font-bold">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(FEATURE_DEFINITIONS).map(([key, feature]) => {
                  const FeatureIcon = feature.icon;
                  return (
                    <tr key={key} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <FeatureIcon className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-white font-medium">{feature.label}</p>
                            <p className="text-xs text-slate-500">{feature.description}</p>
                          </div>
                        </div>
                      </td>
                      {individualPlans.map(plan => (
                        <td key={plan.plan_id} className="text-center p-4">
                          {isFeatureIncluded(plan, key) ? (
                            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                              <Check className="w-5 h-5 text-green-400" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-700/30 flex items-center justify-center mx-auto">
                              <X className="w-5 h-5 text-slate-600" />
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {/* Additional rows */}
                <tr className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-white font-medium">Créditos mensuales</p>
                        <p className="text-xs text-slate-500">Para tests y funciones IA</p>
                      </div>
                    </div>
                  </td>
                  {individualPlans.map(plan => (
                    <td key={plan.plan_id} className="text-center p-4 text-white font-bold">
                      {plan.credits_monthly?.toLocaleString()}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-white font-medium">Exámenes/día</p>
                        <p className="text-xs text-slate-500">Límite diario de exámenes</p>
                      </div>
                    </div>
                  </td>
                  {individualPlans.map(plan => (
                    <td key={plan.plan_id} className="text-center p-4 text-white font-bold">
                      {plan.max_exams_daily === 999 ? 'Ilimitados' : plan.max_exams_daily}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Institutional Plan */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="relative rounded-3xl overflow-hidden ring-2 ring-emerald-500/50"
          data-testid="pricing-card-institutional"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 backdrop-blur-xl" />
          
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center py-2 text-sm font-bold">
            <Building2 className="w-4 h-4 inline mr-1" />
            Para Instituciones y Academias
          </div>

          <div className="relative p-8 pt-14">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Plan Institucional</h3>
                    <p className="text-emerald-300">Solución completa para tu organización</p>
                  </div>
                </div>
                
                <p className="text-slate-300 mb-6">
                  Ideal para academias, hospitales, cuerpos de seguridad y administraciones públicas. 
                  Gestiona todos tus alumnos desde un único panel.
                </p>

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-3xl font-extrabold text-white">Precio personalizado</span>
                </div>

                <Button
                  onClick={() => setShowContactModal(true)}
                  className="w-full md:w-auto px-8 py-6 rounded-xl font-bold text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white"
                  data-testid="contact-institutional-btn"
                >
                  Solicitar Demo
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Users, label: 'Gestión de alumnos', desc: 'Panel centralizado' },
                  { icon: BarChart3, label: 'Analytics avanzados', desc: 'Informes detallados' },
                  { icon: Shield, label: 'SSO / LMS', desc: 'Integración empresarial' },
                  { icon: Headphones, label: 'Soporte dedicado', desc: 'Atención prioritaria' },
                  { icon: Globe, label: 'Multi-sede', desc: 'Varias ubicaciones' },
                  { icon: MapPin, label: 'Multi-oposición', desc: 'Todas las CC.AA.' },
                ].map((item, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <item.icon className="w-6 h-6 text-emerald-400 mb-2" />
                    <p className="text-white font-medium text-sm">{item.label}</p>
                    <p className="text-slate-500 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <div className="mt-16 text-center">
          <p className="text-slate-400">
            ¿Tienes preguntas? <a href="mailto:info@tuplazaia.com" className="text-indigo-400 hover:underline">Contáctanos</a>
          </p>
        </div>
      </div>

      {/* Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Plan Institucional</h2>
                    <p className="text-slate-400 text-sm">Cuéntanos sobre tu institución</p>
                  </div>
                  <button
                    onClick={() => setShowContactModal(false)}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors text-slate-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleContactSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Nombre de la institución *
                  </label>
                  <input
                    type="text"
                    required
                    value={contactForm.company_name}
                    onChange={(e) => setContactForm({...contactForm, company_name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                    placeholder="Hospital General, Academia XYZ..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Nombre de contacto *
                  </label>
                  <input
                    type="text"
                    required
                    value={contactForm.contact_name}
                    onChange={(e) => setContactForm({...contactForm, contact_name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                    placeholder="contacto@institucion.es"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                    placeholder="+34 600 000 000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Número de empleados/alumnos
                  </label>
                  <select
                    value={contactForm.num_employees}
                    onChange={(e) => setContactForm({...contactForm, num_employees: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="1-10">1-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-100">51-100</option>
                    <option value="101-500">101-500</option>
                    <option value="500+">Más de 500</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Mensaje adicional
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all resize-none"
                    placeholder="Cuéntanos más sobre tus necesidades..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-6 rounded-xl font-bold text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Solicitud'
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Al enviar este formulario, aceptas que nos pongamos en contacto contigo 
                  para informarte sobre nuestros servicios institucionales.
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PricingPage;
